import type { ApplicationDraft, EvidenceEntry, Sector } from "../../api/client";
import { BooleanChoice } from "../../components/BooleanChoice";
import { Callout } from "../../components/Callout";
import { DateField } from "../../components/DateField";
import { MultiSelect } from "../../components/MultiSelect";
import { SelectField } from "../../components/SelectField";
import { TextField } from "../../components/TextField";
import { ACCREDITATION_STATUSES } from "../constants";
import { hasStandard, sectorsFor } from "../application";

/**
 * Screen 02 · Accreditation and accredited scope.
 *
 * The scope matrix is the gate everything downstream depends on. `tenant_accredited_scopes`
 * holds one row per (standard, sector) and an audit cannot select a standard, or a sector
 * within it, that has no row — fixtures assertion 3. Accreditation is granted per standard
 * *and* per technical sector, so a standard ticked with no sector accredits nothing and the
 * server refuses it rather than quietly dropping it.
 */
export function AccreditationStep({
  draft,
  set,
  bodies,
  standards,
  sectors,
  errorFor,
  onToggleStandard,
  onToggleSector,
  onSetSectors,
  evidence,
  evidenceBusy,
  evidenceFailure,
  onUploadEvidence,
  onRemoveEvidence,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  bodies: readonly string[];
  standards: readonly string[];
  sectors: readonly Sector[];
  errorFor: (field: string) => string | undefined;
  onToggleStandard: (standard: string) => void;
  onToggleSector: (standard: string, sector: number) => void;
  onSetSectors: (standard: string, sectors: readonly number[]) => void;
  evidence: readonly EvidenceEntry[];
  evidenceBusy: boolean;
  evidenceFailure: string | null;
  onUploadEvidence: (files: File[]) => void;
  onRemoveEvidence: (id: string) => void;
}) {
  const options = sectors.map((sector) => ({ value: sector.index, label: sector.label }));

  return (
    <section className="card">
      <h2>Accreditation</h2>

      <Callout tone="warn" title="This is the gate that matters most.">
        Complao restricts the standards and sectors a certification body can audit to what its
        accreditation actually covers. A body accredited for ISO 9001 in manufacturing cannot
        configure an ISO 45001 audit here.
      </Callout>

      <div className="grid3">
        <SelectField
          id="accreditation_body"
          label="Accreditation body"
          value={draft.accreditation_body ?? ""}
          onChange={(value) => set({ accreditation_body: value })}
          options={bodies.map((name) => ({ value: name, label: name }))}
          error={errorFor("accreditation_body")}
          required
        />
        <TextField
          id="accreditation_number"
          label="Accreditation number"
          value={draft.accreditation_number ?? ""}
          onChange={(value) => set({ accreditation_number: value })}
          error={errorFor("accreditation_number")}
          required
        />
        <SelectField
          id="accreditation_status"
          label="Status"
          value={draft.accreditation_status ?? ""}
          onChange={(value) => set({ accreditation_status: value })}
          options={ACCREDITATION_STATUSES.map((name) => ({ value: name, label: name }))}
          error={errorFor("accreditation_status")}
          required
        />
      </div>

      <div className="grid3">
        <DateField
          id="accreditation_expiry"
          label="Accreditation expiry"
          value={draft.accreditation_expiry ?? ""}
          onChange={(value) => set({ accreditation_expiry: value })}
          error={errorFor("accreditation_expiry")}
          required
        />
        <DateField
          id="next_assessment"
          label="Next scheduled assessment"
          value={draft.next_assessment ?? ""}
          onChange={(value) => set({ next_assessment: value })}
          error={errorFor("next_assessment")}
        />
        <BooleanChoice
          id="mla_signatory"
          label="IAF MLA signatory"
          value={draft.mla_signatory ?? null}
          onChange={(value) => set({ mla_signatory: value })}
        />
      </div>

      <hr className="hr" />

      <div className="eyebrow">Accredited scope — standard by sector</div>
      <p className="sub" style={{ marginBottom: "12px" }}>
        Tick each sector your accreditation covers for that standard. Accreditation is granted
        per standard and per technical sector, not as a blanket permission.
      </p>

      {errorFor("scopes") ? (
        <p className="err" role="alert">
          {errorFor("scopes")}
        </p>
      ) : null}

      {standards.map((standard) => {
        const selected = hasStandard(draft, standard);
        const chosen = sectorsFor(draft, standard);
        const error = errorFor(`scopes[${standard}]`);
        const key = standard.replace(/\W+/g, "");

        return (
          <div className="scope" key={standard}>
            <label htmlFor={`standard-${key}`}>
              <input
                type="checkbox"
                id={`standard-${key}`}
                checked={selected}
                onChange={() => onToggleStandard(standard)}
              />
              {standard}
            </label>

            {selected ? (
              <>
                <MultiSelect
                  id={`sectors-${key}`}
                  options={options}
                  selected={chosen}
                  onToggle={(sector) => onToggleSector(standard, sector)}
                  onSetAll={(sectors) => onSetSectors(standard, sectors)}
                  placeholder="Select the IAF technical sectors this accreditation covers"
                  searchLabel="Filter sectors"
                  invalid={Boolean(error)}
                />
                {error ? (
                  <p className="err" role="alert">
                    {error}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}

      <hr className="hr" />

      <EvidenceField
        evidence={evidence}
        busy={evidenceBusy}
        failure={evidenceFailure}
        onUpload={onUploadEvidence}
        onRemove={onRemoveEvidence}
      />
    </section>
  );
}

/**
 * Evidence — the accreditation certificate and the insurance schedule.
 *
 * The bytes are uploaded as they are chosen, not held until submission. M1-1 could only
 * record filenames because there was no tenant to address them to; ADR 0013 changed that,
 * since a certification body registers before it applies. Content-addressed, so the file a
 * Complao administrator checks against the accreditation body's public register cannot
 * later be substituted under the same key — and attaching identical bytes twice is one
 * attachment rather than two rows naming one object.
 */
function EvidenceField({
  evidence,
  busy,
  failure,
  onUpload,
  onRemove,
}: {
  evidence: readonly EvidenceEntry[];
  busy: boolean;
  failure: string | null;
  onUpload: (files: File[]) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="field">
      <label className="f" htmlFor="evidence">
        Evidence <i>accreditation certificate and insurance schedule</i>
      </label>
      <label className="dropzone" htmlFor="evidence">
        <div className="up" aria-hidden="true">
          ↑
        </div>
        <div className="lead">
          {busy ? "Uploading…" : "Click to upload or drag files here"}
        </div>
        <div className="kind">
          PDF · verified by Complao against the accreditation body&rsquo;s register
        </div>
      </label>
      <input
        id="evidence"
        type="file"
        multiple
        accept="application/pdf"
        className="visually-hidden"
        onChange={(event) => {
          onUpload(Array.from(event.target.files ?? []));
          // Cleared so choosing the same file again still fires a change.
          event.target.value = "";
        }}
      />
      {failure ? (
        <p className="err" role="alert">
          {failure}
        </p>
      ) : null}
      {evidence.map((entry) => (
        <div className="docrow" key={entry.id}>
          <span className="mono">{entry.filename}</span>
          <span className="pill p-ok" style={{ marginLeft: "auto" }}>
            Attached
          </span>
          <button type="button" className="btn sec sm" onClick={() => onRemove(entry.id)}>
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}
